import React, { FC, Dispatch, useEffect } from 'react'
import { Header } from '@lightspeed/design-system-react'
import { removeFromStorage, trackUserEvent } from '@vend/utilities'
import { useT } from '@transifex/react'
import { PaymentEvent } from '../hooks/use-lspay-payment-server-reducer/types'
import { LSPAYModalContent } from '../../../modal-states/LSPAYModalContent'

import {
  LSPAY_ERROR,
  LSPAY_TRANSACTION_STATUS,
  SWIPER_CANCEL_REASON,
} from '../../../../helpers/lspayConstants'
import {
  getTransaction,
  isTransactionCompleted,
} from '../../../../helpers/lspayTransaction'
import {
  getPaymentDeclinedError,
  isCardSpecificDecline,
} from '../../../../helpers/errorChecking'
import { TAG, trackLSPayServerError } from '../../../../hooks/errorTracking'
import { WEB_REGISTER } from '../../../../../../web-register.constant'
import { SwiperError } from '../../../../helpers/lspay.types'
import { ACTIVE_LSPAY_TRANSACTION } from '../../../../../../../constants/payments'
import { useErrorMessages } from '../../../../hooks/useErrorMessages'

interface RecoverTransactionProps {
  paymentIntentId?: string
  registerId: string
  registerSalePaymentId: string
  registerSaleId: string
  dispatch: Dispatch<PaymentEvent>
  setDisableCNP: (disableCNP: boolean) => void
}

export const RecoverTransaction: FC<RecoverTransactionProps> = ({
  paymentIntentId,
  registerId,
  registerSalePaymentId,
  registerSaleId,
  dispatch,
  setDisableCNP,
}) => {
  const getErrorMessage = useErrorMessages()
  const t = useT()
  useEffect(() => {
    setDisableCNP(true)
    getTransaction({
      registerId,
      paymentIntentId,
      registerSalePaymentId,
    })
      .then(data => {
        // Track the event for successful retrieval of recovery transaction
        trackUserEvent(
          WEB_REGISTER.DISPLAY_NAME,
          'LSD recovered transaction status',
          {
            registerSalePaymentId,
            registerSaleId,
            paymentIntentId,
            status: data.status,
          }
        )
        if (data.status === LSPAY_TRANSACTION_STATUS.PROCESSING) {
          // satrt polling
          dispatch({
            type: 'START_POLLING',
            paymentIntentId: data.payment_intent_id,
          })

          return
        }
        if (isTransactionCompleted(data)) {
          dispatch({
            type: 'POLL_RESULT_OK',
            transaction: data.transaction,
          })
          return
        }
        if (data.status === LSPAY_TRANSACTION_STATUS.AUTHORISED) {
          dispatch({
            type: 'AUTHORISED',
            paymentIntentId: data.payment_intent_id,
          })
          return
        }
        if (data.status === LSPAY_TRANSACTION_STATUS.DECLINED) {
          // remove the active lspay transaction from storage for declined transactions
          removeFromStorage(ACTIVE_LSPAY_TRANSACTION)

          dispatch({
            type: 'DECLINED',
            errorMessage: getErrorMessage(
              getPaymentDeclinedError(data.reason),
              isCardSpecificDecline(data.reason)
            ),
            paymentIntentId: data.payment_intent_id,
          })
          return
        }
        if (data.status === LSPAY_TRANSACTION_STATUS.CANCELLED) {
          if (data.reason === SWIPER_CANCEL_REASON.PAYMENT_INTENT_NOT_FOUND) {
            trackUserEvent(
              WEB_REGISTER.DISPLAY_NAME,
              'LSD recovery failed: intent was not found',
              {
                registerSalePaymentId,
                registerSaleId,
              }
            )
          }
          dispatch({
            type: 'CANCELLED',
            errorMessage: getErrorMessage(LSPAY_ERROR.CANCEL_PAYMENT),
            paymentIntentId: data.payment_intent_id,
            reason: data.reason,
          })
          return
        }
      })
      .catch((error: SwiperError) => {
        // track the error
        trackLSPayServerError(
          '[lspay-sever-driven][recovery] failed to get transaction',
          error,
          {
            registerSalePaymentId,
            paymentIntentId,
          },
          TAG.payment
        )
        dispatch({
          type: 'ERROR',
          errorMessage: getErrorMessage(LSPAY_ERROR.DEFAULT_PAYMENT),
          swiperError: error,
        })
        return
      })
      .finally(() => setDisableCNP(false))
  }, [
    paymentIntentId,
    registerId,
    registerSalePaymentId,
    dispatch,
    setDisableCNP,
    registerSaleId,
    getErrorMessage,
  ])

  return (
    <LSPAYModalContent>
      <Header type="sub-heading" level="2">
        {t('Checking payment status...', {
          _context: 'Payment recoverying step header',
        })}
      </Header>
    </LSPAYModalContent>
  )
}
